import { useMemo } from 'react'
import useLocalStorage from './useLocalStorage'
import { Build } from '../v2/components/Footer/Version'

type StoredPreference = {
    value: boolean
    version: string
}

export const currentVersion = (process.env.BUILD as unknown as Build).version

/**
 * Stores the user's preference for the new ("v2") UI. The preference is scoped to the current release version
 * (taken from package.json at build time): when a new version is shipped, the stored preference is treated as
 * expired and the default ('new UI on') is restored. This means a user who opts out of the new UI will be
 * shown the new UI again after the next release.
 */
export default function useNewVersionPreference(): {
    loaded: boolean
    newVersion: boolean
    setNewVersion: (value: boolean) => void
} {
    const { loaded, values, setValues } = useLocalStorage<StoredPreference, unknown>({
        key: 'newVersion',
        defaultValue: undefined,
        fromJson: raw => {
            // Legacy entries were stored as a raw boolean. Treat any non-matching shape (including legacy
            // booleans and entries set against an older release) as expired so the default kicks in.
            if (raw && typeof raw === 'object' && 'version' in raw && (raw as StoredPreference).version === currentVersion) {
                return raw as StoredPreference
            }
            return undefined
        },
        toJson: values => values,
    })

    const newVersion = useMemo<boolean>(() => values?.value ?? true, [values])

    const setNewVersion = (value: boolean) => setValues({ value, version: currentVersion })

    return { loaded, newVersion, setNewVersion }
}
